import type { Activity } from '../model/activity.js'
import { estimated, original, type SummaryValue, unavailable } from './provenance.js'

/**
 * Calorie/physiology-value handling (`summary-calculation` spec:
 * "Calorie and physiology-derived values are never fabricated"). A
 * device's calorie figure comes from a proprietary algorithm this
 * project cannot reproduce, so recalculation is never attempted here —
 * only a disclosed proportional estimate (for a split, scaled by how
 * much of the original timer time the segment represents) or an
 * explicit `unavailable`, never a silent guess.
 */
export function computeSplitCalories(
  originalTotalCalories: number | undefined,
  segmentFractionOfOriginal: number,
): SummaryValue<number> {
  if (originalTotalCalories == null) return unavailable()
  return estimated(Math.round(originalTotalCalories * segmentFractionOfOriginal))
}

function allHaveCalories(
  messages: Array<Record<string, unknown>>,
): messages is Array<Record<string, unknown> & { totalCalories: number }> {
  return (
    messages.length > 0 &&
    messages.every((m) => typeof m.totalCalories === 'number' && Number.isFinite(m.totalCalories))
  )
}

/**
 * Total calories for the activity, for the dashboard summary. FIT record
 * messages carry no per-record calories, so this only ever *reads* the
 * device's own summary messages — it never computes a calorie figure:
 *
 * 1. Every session carries `totalCalories` -> their sum, `original`.
 * 2. Otherwise every lap carries `totalCalories` -> their sum, `original`
 *    (a partial session set would silently undercount, so it falls
 *    through to laps rather than summing what's there).
 * 3. Otherwise `unavailable`.
 *
 * Known limitation: after crop/removeInterval/split,
 * `calc/boundaries.ts` rewrites a narrowed lap/session `totalCalories`
 * as a proportional estimate (`computeSplitCalories`) without leaving
 * any marker on the message, so this function cannot distinguish that
 * from a device value and still reports `original`. A caller that knows
 * the edit history (e.g. any record-removing op is active) should
 * relabel the result `estimated`.
 */
export function computeTotalCalories(activity: Activity): SummaryValue<number> {
  if (allHaveCalories(activity.sessions)) {
    return original(activity.sessions.reduce((sum, s) => sum + s.totalCalories, 0))
  }
  if (allHaveCalories(activity.laps)) {
    return original(activity.laps.reduce((sum, l) => sum + l.totalCalories, 0))
  }
  return unavailable()
}
